import { AuthTokens, TokenPayload, AuthEvent } from './types';
import { AUTH_CONFIG } from './config';

// Storage keys for token storage
const ACCESS_TOKEN_KEY = 'access-token';
const REFRESH_TOKEN_KEY = 'refresh-token';

// Determine where tokens are currently stored (local vs session)
// Preference order: localStorage first (persistent), then sessionStorage (session-only)
const getActiveStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined') return null;

    const ls = window.localStorage;
    const ss = window.sessionStorage;

    const lsHas = !!ls.getItem(REFRESH_TOKEN_KEY) || !!ls.getItem(ACCESS_TOKEN_KEY);
    if (lsHas) return ls;

    const ssHas = !!ss.getItem(REFRESH_TOKEN_KEY) || !!ss.getItem(ACCESS_TOKEN_KEY);
    if (ssHas) return ss;

    return null;
  } catch (error) {
    return null;
  }
};

const clearFromBothStorages = (): void => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    window.sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    // no-op
  }
};

// Private state for preventing duplicate refresh calls
let currentRefreshPromise: Promise<boolean> | null = null;

/**
 * Check if token is valid and not expired
 */
export const isTokenValid = (token: string | null): boolean => {
  if (!token) return false;

  const payload = decodeToken(token);
  if (!payload || !payload.exp) return false;

  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp > currentTime;
};

/**
 * Check if access token expires soon
 */
export const isAccessTokenExpiringSoon = (token: string | null): boolean => {
  if (!token) return true;

  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;

  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = (payload.exp - currentTime) * 1000; // Convert to milliseconds

  return timeUntilExpiry <= AUTH_CONFIG.accessTokenRefreshThresholdMs;
};

/**
 * Check if refresh token expires soon
 */
export const isRefreshTokenExpiringSoon = (token: string | null): boolean => {
  if (!token) return true;

  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;

  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = (payload.exp - currentTime) * 1000; // Convert to milliseconds

  return timeUntilExpiry <= AUTH_CONFIG.refreshTokenRefreshThresholdMs;
};

/**
 * Get tokens from storage
 */
export const getTokens = (): AuthTokens | null => {
  try {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    if (!accessToken || !refreshToken) return null;

    return { accessToken, refreshToken };
  } catch (error) {
    console.error('Failed to get tokens from storage:', error);
    return null;
  }
};

/**
 * Store authentication tokens in localStorage
 */
export const setTokens = (tokens: AuthTokens, options?: { persistent?: boolean }): void => {
  try {
    // Decide destination storage
    let destination: Storage | null = null;

    if (options && typeof options.persistent === 'boolean') {
      destination = options.persistent ? window.localStorage : window.sessionStorage;
    } else {
      // If tokens already exist, keep using the same storage
      destination = getActiveStorage() || window.localStorage; // default to local for backward compatibility
    }

    // Clear from both to avoid duplicates
    clearFromBothStorages();

    destination.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    destination.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    
    // Emit token refresh event for cross-tab synchronization
    emitAuthEvent(AuthEvent.TOKEN_REFRESH, { 
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken 
    });
  } catch (error) {
    console.error('Failed to store tokens:', error);
    throw new Error('Token storage failed');
  }
};

/**
 * Clear all tokens from storage
 */
export const clearTokens = (): void => {
  try {
    clearFromBothStorages();
    
    // Emit logout event for cross-tab synchronization
    emitAuthEvent(AuthEvent.LOGOUT);
  } catch (error) {
    console.warn('Failed to clear tokens:', error);
  }
};

/**
 * Get access token from storage
 */
export const getAccessToken = (): string | null => {
  try {
    const active = getActiveStorage();
    if (active) return active.getItem(ACCESS_TOKEN_KEY);
    // Fallback check
    return (
      window.localStorage.getItem(ACCESS_TOKEN_KEY) ||
      window.sessionStorage.getItem(ACCESS_TOKEN_KEY)
    );
  } catch (error) {
    return null;
  }
};

/**
 * Get refresh token from storage
 */
export const getRefreshToken = (): string | null => {
  try {
    const active = getActiveStorage();
    if (active) return active.getItem(REFRESH_TOKEN_KEY);
    // Fallback check
    return (
      window.localStorage.getItem(REFRESH_TOKEN_KEY) ||
      window.sessionStorage.getItem(REFRESH_TOKEN_KEY)
    );
  } catch (error) {
    return null;
  }
};

/**
 * Check if user is currently authenticated
 */
export const isAuthenticated = (): boolean => {
  const tokens = getTokens();
  if (!tokens) return false;

  // User is authenticated if refresh token is valid (even if access token expired)
  return isTokenValid(tokens.refreshToken);
};

/**
 * Simplified token validation flow:
 * 
 * BEFORE EVERY API CALL:
 * 2.1 - Check if access token exists and hasn't expired
 * 2.2 - If valid, use it 
 * 2.3 - If expired, check refresh token expiration
 * 2.4 - If refresh token valid, call auth/refresh endpoint
 * 2.5 - If refresh token expired, logout and clear storage 
 */
export const validateTokensForApiCall = async (
  refreshFunction: () => Promise<AuthTokens>,
  logoutFunction: () => void
): Promise<boolean> => {
  const tokens = getTokens();
  
  if (!tokens) return false;

  if (isTokenValid(tokens.accessToken)) {
    return true;
  }

  if (!isTokenValid(tokens.refreshToken)) {
    clearTokens();
    logoutFunction();
    return false;
  }

  // Step 2.5: Refresh token valid, use auth/refresh endpoint
  try {
    const success = await executeRefresh(refreshFunction);
    if (success) {
      return true;
    } else {
      // Refresh failed, logout
      clearTokens();
      logoutFunction();
      return false;
    }
  } catch (error) {
    console.error('Token refresh error:', error);
    clearTokens();
    logoutFunction();
    return false;
  }
};

/**
 * Execute refresh with deduplication
 */
export const executeRefresh = async (refreshFunction: () => Promise<AuthTokens>): Promise<boolean> => {
  if (currentRefreshPromise) {
    return currentRefreshPromise;
  }

  currentRefreshPromise = performRefresh(refreshFunction);
  
  try {
    return await currentRefreshPromise;
  } finally {
    currentRefreshPromise = null;
  }
};

/**
 * Internal refresh implementation
 */
const performRefresh = async (refreshFunction: () => Promise<AuthTokens>): Promise<boolean> => {
  try {
    const newTokens = await refreshFunction();
    setTokens(newTokens);
    return true;
  } catch (error) {
    console.error('Token refresh failed:', error);
    clearTokens();
    emitAuthEvent(AuthEvent.SESSION_EXPIRED);
    return false;
  }
};

/**
 * Setup cross-tab authentication synchronization
 */
export const setupCrossTabSync = (onAuthEvent: (event: AuthEvent, data?: any) => void): (() => void) => {
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === 'auth_event' && event.newValue) {
      try {
        const eventData = JSON.parse(event.newValue);
        onAuthEvent(eventData.event as AuthEvent, eventData.data);
      } catch (error) {
        console.error('Failed to parse auth event:', error);
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
};

/**
 * Decode JWT token payload
 */
const decodeToken = (token: string | null): TokenPayload | null => {
  try {
    if (!token) return null;
    
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));

    return JSON.parse(decoded) as TokenPayload;
  } catch (error) {
    console.warn('Failed to decode token:', error);
    return null;
  }
};

/**
 * Emit authentication events for cross-tab communication
 */
const emitAuthEvent = (event: AuthEvent, data?: any): void => {
  try {
    // Dispatch a DOM event named exactly as the enum value
    window.dispatchEvent(new CustomEvent(event, { detail: data }));
    
    // Also use localStorage event for cross-tab communication
    localStorage.setItem('auth_event', JSON.stringify({ event, data, timestamp: Date.now() }));
    localStorage.removeItem('auth_event'); // Trigger storage event
  } catch (error) {
    console.warn('Failed to emit auth event:', error);
  }
};
 